import React from 'react';
import {
  Box,
  VStack,
  Text,
  Icon,
  HStack,
  CircularProgress,
} from '@chakra-ui/react';
import { FiMapPin, FiCheck } from 'react-icons/fi';
import { FoodfluenceLocation } from '@foodfluence/shared';
import { ff } from '../theme';

interface LocationListProps {
  locations: FoodfluenceLocation[];
  title?: string;
  isLoading?: boolean;
  error?: string | null;
  isSelectable?: boolean;
  selectedLocationIds?: string[];
  onLocationClick?: (docId: string) => void;
}

const LocationList: React.FC<LocationListProps> = ({
  locations,
  title = "Locations",
  isLoading = false,
  error = null,
  isSelectable = false,
  selectedLocationIds = [],
  onLocationClick,
}) => {
  return (
    <Box>
      {title && (
        <Text textStyle="eyebrow" color={ff.text2} mb="10px">
          {title}
        </Text>
      )}

      <VStack align="stretch" spacing={2}>
        {isLoading ? (
          <Box py={6} textAlign="center">
            <CircularProgress isIndeterminate color={ff.ink} trackColor={ff.line} size="32px" />
            <Text color={ff.text2} fontSize="14px" mt={2}>Loading locations...</Text>
          </Box>
        ) : error ? (
          <Box py={4} textAlign="center">
            <Text color={ff.danger} fontSize="14px">There was an error loading locations</Text>
          </Box>
        ) : locations.length === 0 ? (
          <Box py={4} textAlign="center">
            <Text color={ff.text2} fontSize="14px">No locations available</Text>
          </Box>
        ) : (
          locations.map((location) => {
            const isSelected = isSelectable && selectedLocationIds.includes(location.docId);
            return (
              <Box
                key={location.docId}
                bg={isSelected ? ff.limeSurface : 'white'}
                borderWidth="1px"
                borderColor={isSelected ? ff.ink : ff.line}
                borderRadius="20px"
                px={4}
                py={3}
                minH="72px"
                display="flex"
                alignItems="center"
                transition="all 0.15s"
                cursor={isSelectable ? "pointer" : "default"}
                onClick={isSelectable && onLocationClick ? () => onLocationClick(location.docId) : undefined}
                _hover={isSelectable && !isSelected ? { borderColor: ff.border } : undefined}
              >
                <HStack spacing={3} align="center" w="100%">
                  <Box
                    bg={isSelected ? ff.ink : ff.grey}
                    w="40px"
                    h="40px"
                    borderRadius="12px"
                    color={isSelected ? ff.lime : ff.ink}
                    flexShrink={0}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Icon as={isSelected ? FiCheck : FiMapPin} boxSize={4} />
                  </Box>
                  <VStack align="start" spacing={0} flex="1" minW={0}>
                    {location.name && (
                      <Text fontSize="15px" fontWeight="600" color={ff.ink} noOfLines={1}>
                        {location.name}
                      </Text>
                    )}
                    <Text fontSize="14px" color={ff.body} noOfLines={1}>
                      {location.streetNumber} {location.streetName}
                    </Text>
                    <Text fontSize="13px" color={ff.text2}>
                      {location.city}, {location.state} {location.zip}
                    </Text>
                  </VStack>
                </HStack>
              </Box>
            );
          })
        )}
      </VStack>
    </Box>
  );
};

export default LocationList;
